import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '../../../environments/environment';
import { CCEDewPoint, CCEDewPointVolume,  CCEDewPointVolumePartialSave } from '../models/cce-dewpoint';
import { CCEDewPointResult } from '../models/cce-dewpoint';
import { APIResponse } from '../../models/APIResponse';
import { Chart, Plugin, ScaleOptionsByType } from 'chart.js/auto';
import { StatusParams } from '../../msst/models/msst';
import { BPTable, DPTable } from '../../models/lab-test';
import { cceDPConstants } from '../../common/experiment-constants';
import { NUMBERS_CONSTANTS } from '../../common/numbers-constants';
import { apiV1 } from '../../common/texts-constants';
import { ChartAxisDefault } from '../../cce/models/cce';


@Injectable({
  providedIn: 'root'
})
export class CceDewpointService {
  private http = inject(HttpClient);
  router = inject(Router);


  private apiBaseUrl = environment.apiUrl+apiV1;
  private volumeDatasource = new BehaviorSubject<CCEDewPointVolume[]>([]);
  currentVolumeData = this.volumeDatasource.asObservable();
  public statusChange$= new BehaviorSubject<StatusParams>({statusId:0,statusName:''});

  constructor() {
    const router = this.router;

    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        //handle navigation end
      }
    });
  }

  // Api call to save CCEDewPoint test details 
    SaveCCEDewPoint(ccedp: CCEDewPoint) {
      return this.http.post<APIResponse<CCEDewPoint>>(this.apiBaseUrl + cceDPConstants.saveCceDewpoint, ccedp);
    }

    //Partial Save and Update CCE Dewpoint Volume details
    PartialSaveAndUpdateCCE(cceDewpointVolumeDetails: CCEDewPointVolumePartialSave) {
      return this.http.post<APIResponse<CCEDewPointVolumePartialSave>>(this.apiBaseUrl + cceDPConstants.partialSaveAndUpdateCceDewpoint, cceDewpointVolumeDetails);
    }

    // Api call to Get CCEDewPoint test details
    GetCCEDewPoint(testRepeatId: number) {
      return this.http.get<APIResponse<CCEDewPoint>>(this.apiBaseUrl + cceDPConstants.getCceDewpoint + testRepeatId);
    }
  
    // Api call to Get CCE Dewpoint test details
    GetCCEDewPointReport(testRepeatId: number) {
      return this.http.get<APIResponse<CCEDewPoint>>(this.apiBaseUrl + cceDPConstants.getCceDewpointForFinalReport + testRepeatId);
    }

    // Api call to Update CCE Dewpoint test details
    UpdateCCEDewPoint(ccedp: CCEDewPoint) {
      return this.http.post<APIResponse<CCEDewPoint>>(this.apiBaseUrl + cceDPConstants.updateCceDewpoint, ccedp);
    }
    // Api call to Get LiquidVolume Calculation 
    GetLiquidVolumeCalculation(volumeInput: CCEDewPointVolume[]) {
      return this.http.post<APIResponse<CCEDewPointVolume[]>>(this.apiBaseUrl + cceDPConstants.liquidVolumeCalculation, volumeInput)
    }
  
    // Round number to decimal places 
    RoundTo(num: number, places: number) {
      const factor = 10 ** places;
      return Math.round(num * factor) / factor;
    }

    CreateChangeSnapshot(value: unknown): string {
      return JSON.stringify(this.NormalizeForComparison(value));
    }

    private NormalizeForComparison(value: unknown): unknown {
      if (Array.isArray(value)) {
        return value.map(item => this.NormalizeForComparison(item));
      }

      if (value !== null && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [
          key,
          this.NormalizeForComparison(item)
        ]));
      }

      if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
      }

      return value;
    }

    // Update Volume Data Source
    UpdateVolumeData(newData: CCEDewPointVolume[]) {
    this.volumeDatasource.next(newData);
    }

    // Clear Volume Data Source
    ClearVolumeData() {
    this.volumeDatasource.next([]);
    this.UpdateVolumeData([]);
    }

    UpdateStatus(status: StatusParams) {
    this.statusChange$.next(status);
    }

  BuildReportListData(rows: CCEDewPointResult[]): CCEDewPointResult[] {
    return rows.map((obj: CCEDewPointResult) => ({
      pressure: obj.pressure,
      pointName: obj.pointName,
      cellVolume: obj.cellVolume,
      vVSat: obj.vVSat,
      lVMassDens: obj.lVMassDens,
      zFactor: obj.zFactor,
      liquidVolumeStg: obj.liquidVolumeStg,
      liquidVolumeSat: obj.liquidVolumeSat,
      compressibility: obj.compressibility,
      liquidVolume: obj.liquidVolume
    }));
  }

  // Create Scatter Chart For CCE Calculation
  CreateScatterChartForCCECalc(ctx: CanvasRenderingContext2D, originalVSatData: {x: number; y:number}[],
    DataSetlabels: string, PlotTitle: string, xAxisTitle: string, yAxisTitle: string): Chart {
    return new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: DataSetlabels,
            data: originalVSatData,
            borderColor: '#5B9BD5',
            backgroundColor: '#5B9BD5',
            showLine: false,
            fill: false,
            borderWidth: 1,
            pointStyle: 'rectRot',
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: PlotTitle
          },
          legend: {
            labels: {
              usePointStyle: true,
            },
            position: 'right',
          },
          datalabels: {
            display: false
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true
              },
              pinch: {
                enabled: true
              },
              mode: 'xy'
            },
            pan: {
              enabled: true,
              mode: 'xy'
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: xAxisTitle,
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
              },
              color: '#000000',
            },
            grid: {
              display: false,
            },
          },
          y: {
            title: {
              display: true,
              text: yAxisTitle,
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
              },
            },
            grid: {
              display: false,
            },
          },
        },
      },
    });
  }

  // Create Scatter Chart For CCE Calculation With Min Max
  CreateScatterChartForCCECalcWithMinMax(ctx: CanvasRenderingContext2D, originalVSatData: {x: number; y:number}[],
    DataSetlabels: string, PlotTitle: string, xAxisTitle: string, yAxisTitle: string, ymin: number, ymax: number): Chart {
    return new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: DataSetlabels,
            data: originalVSatData,
            borderColor: '#5B9BD5',
            backgroundColor: '#5B9BD5',
            showLine: false,
            fill: false,
            borderWidth: 1,
            pointStyle: 'rectRot',
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: PlotTitle
          },
          legend: {
            labels: {
              usePointStyle: true,
            },
            position: 'right',
          },
          datalabels: {
            display: false
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true
              },
              pinch: {
                enabled: true
              },
              mode: 'xy'
            },
            pan: {
              enabled: true,
              mode: 'xy'
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: xAxisTitle,
            },
            beginAtZero: true,
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
              },
              color: '#000000',
            },
            grid: {
              display: false,
            },
          },
          y: {
            title: {
              display: true,
              text: yAxisTitle,
            },
            min: ymin,     // Math.floor(ymin * 100) / 100 - Calculated in CalculateL_VMassDensityMinAndMax()
            max: ymax,    //Math.ceil(ymax * 100) / 100 - Calculated in CalculateL_VMassDensityMinAndMax()
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
              },
            },
            grid: {
              display: false,
            },
          },
        },
      },
    });
  }

   boxBorderPlugin: Plugin = {
    id: 'boxBorderPlugin',
    beforeDraw: (chart: Chart) => {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      ctx.save();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = NUMBERS_CONSTANTS.four;

      // Top border
      ctx.beginPath();
      ctx.moveTo(chartArea.left, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.top);
      ctx.stroke();

      ctx.lineWidth = NUMBERS_CONSTANTS.three;
      // Right border
      ctx.beginPath();
      ctx.moveTo(chartArea.right, chartArea.top);
      ctx.lineTo(chartArea.right, chartArea.bottom);
      ctx.stroke();

      ctx.restore();
    }
  };
  axisOriginTicksPlugin: Plugin = {
    id: 'axisOriginTicksPlugin',
    beforeDraw: (chart: Chart) => {
      const ctx = chart.ctx;
      const xScale = chart.scales['x'];
      const yScale = chart.scales['y'];
      const tickLength = 8;
      ctx.save();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = NUMBERS_CONSTANTS.two;
      // Draw ticks on X axis (inside plot area only)
      xScale.ticks.forEach(tick => {
        const x = xScale.getPixelForValue(tick.value);
        const yEnd = chart.chartArea.bottom;

        if (x >= chart.chartArea.left && x <= chart.chartArea.right) {
          ctx.beginPath();
          ctx.moveTo(x, yEnd - tickLength); // bottom edge inward
          ctx.lineTo(x, yEnd);              // bottom edge
          ctx.stroke();
        }
      });

      // Draw ticks on Y axis (inside plot area only)
      yScale.ticks.forEach(tick => {
        const y = yScale.getPixelForValue(tick.value);
        const xStart = chart.chartArea.left;

        if (y >= chart.chartArea.top && y <= chart.chartArea.bottom) {
          ctx.beginPath();
          ctx.moveTo(xStart, y);              // left edge
          ctx.lineTo(xStart + tickLength, y); // left edge inward
          ctx.stroke();
        }
      });

      ctx.restore();

    }
  };



  // Create graphs for Plots Gas
  CreateChartPlotsGasReport(bubbletableData: BPTable[], ctxPO1: CanvasRenderingContext2D, ctxPO2: CanvasRenderingContext2D) {
    const objArray = [{}];
    const objNewArray = [{}];
    for (let n = 0; bubbletableData.length > n; n++) {
      objArray.push({ x: bubbletableData[n].pressure, y: bubbletableData[n].vVSat })
      objNewArray.push({ x: bubbletableData[n].pressure, y: bubbletableData[n].liquidVolume })
    }
    //For Pressure V/Vsat chart
    const pressureV_VsatChart = new Chart(ctxPO1, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: '',
            data: objArray,
            borderColor: '#000000ff',
            backgroundColor: '#f67803ff',
            showLine: false,
            fill: false,
            borderWidth: 3,
            pointStyle: 'circle',
            pointRadius: 9,
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 30
            },
            color: '#000000'
          },
          legend: {
            // labels: {
            //   usePointStyle: true,
            // },
            // position: 'left',
            display: false
          },
          datalabels: {
            display: false
          }
        },
        animation: false,
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: 2
            },
            title: {
              display: true,
              text: 'Pressure (psia)',
              font: {
                size: 40,
                weight: 'bold'
              },
              color: '#000000'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 35
              },
              color: '#000000',
              stepSize: 2000
            },
            min: 0,
            max: 14000,
            grid: {
              display: false,
            },

          },
          y: {
            title: {
              display: true,
              text: 'V/Vsat',
              font: {
                size: 40,
                weight: 'bold'
              },
              color: '#000000'
            },
            border: {
              display: true,
              color: '#000000',
              width: 2
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 35
              },
              stepSize: 0.1
            },
            min: 0.9,
            max: 1.4,
            grid: {
              display: false,
            },
          },
        },
        elements: {
          point: {
            radius: 8,
            hoverRadius: 9,
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
    //For Pressure Liquid volume chart
    const pressureVolumeChart = new Chart(ctxPO2, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: '',
            data: objNewArray,
            borderColor: '#000000ff',
            backgroundColor: '#f67803ff',
            showLine: false,
            fill: false,
            borderWidth: 3,
            pointStyle: 'circle',
            pointRadius: 9,
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 40,
              weight: 'bold'
            },
            color: '#000000'
          },
          legend: {
            display: false
          },
          datalabels: {
            display: false
          }
        },
        animation: false,
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: 3
            },
            title: {
              display: true,
              text: 'Pressure (psia)',
              font: {
                size: 40,
                weight: 'bold'
              },
              color: '#000000'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 30
              },
              color: '#000000',
              stepSize: 2000
            },
            min: 0,
            max: 14000,
            grid: {
              display: false,
            },

          },
          y: {
            title: {
              display: true,
              text: 'Liquid Vol (%)',
              font: {
                size: 40,
                weight: 'bold'
              },
              color: '#000000'
            },
            border: {
              display: true,
              color: '#000000',
              width: 3
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 30
              },
              stepSize: 10
            },
            min: 0,
            max: 110,
            grid: {
              display: false,
            },
          },
        },
        elements: {
          point: {
            radius: 8,
            hoverRadius: 9,
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
    return [pressureV_VsatChart, pressureVolumeChart];
  }   

  // Create graphs for Plots Gas
  CreateDewPointChartPlotsGasReport(cceDewPointData: DPTable[], ctxPO1: CanvasRenderingContext2D, ctxPO2: CanvasRenderingContext2D) {
    const objArray = [{}];
    const objNewArray = [{}];
    for (let n = 0; cceDewPointData.length > n; n++) {
      objArray.push({ x: cceDewPointData[n].pressure, y: cceDewPointData[n].vVSat })
      objNewArray.push({ x: cceDewPointData[n].pressure, y: cceDewPointData[n].liquidVolumeStg })
    }

    /*Start Min&Max calculations for V/Vsat and Liquid Volume*/
    let minVsatY = Number.MAX_VALUE;
    let maxVsatY = Number.MIN_VALUE;
    let minLiquidVolumeY = Number.MAX_VALUE;
    let maxLiquidVolumeY = Number.MIN_VALUE;
    let maxPressureX = Number.MIN_VALUE;

    for (const item of cceDewPointData) {
      if (item.vVSat !== undefined) {
        minVsatY = Math.min(minVsatY, item.vVSat);
        maxVsatY = Math.max(maxVsatY, item.vVSat);
      }
      if (item.liquidVolumeStg !== undefined) {
        minLiquidVolumeY = Math.min(minLiquidVolumeY, item.liquidVolumeStg);
        maxLiquidVolumeY = Math.max(maxLiquidVolumeY, item.liquidVolumeStg);
      }
      if (item.pressure !== undefined) {
        maxPressureX = Math.max(maxPressureX, item.pressure);
      }
    }
    const yMin = Math.floor((minVsatY - 0.1) * 10) / 10;
    const yMax = Math.ceil((maxVsatY + 0.1) * 10) / 10;
    const xMax = maxPressureX>=13900 ? 16000 : 14000;
    const yMaxLiquidVolume = (Math.ceil(maxLiquidVolumeY / 10) * 10)+10;
    const yMinLiquidVolume = minLiquidVolumeY >= 1 ? 0 : minLiquidVolumeY<0? Math.ceil(minLiquidVolumeY)-10: Math.floor(minLiquidVolumeY)-10;
    /*End Min&Max calculations for V/Vsat and Liquid Volume*/
    //For Pressure V/Vsat chart
    const pressureV_VsatChart = new Chart(ctxPO1, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: '',
            data: objArray,
            borderColor: '#000000ff',
            backgroundColor: '#c55a11',
            showLine: false,
            fill: false,
            borderWidth: NUMBERS_CONSTANTS.three,
            pointStyle: 'circle',
            pointRadius: NUMBERS_CONSTANTS.eleven,
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 30
            },
            color: '#000000'
          },
          legend: {
            display: false
          },
          datalabels: {
            display: false
          }
        },
        animation: false,
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: NUMBERS_CONSTANTS.three
            },
            title: {
              display: true,
              text: 'Pressure (psia)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 35
              },
              color: '#000000',
              stepSize: 2000,
              callback: (value) => value.toString()
            },
            min: 0,
            max: xMax,
            grid: {
              display: false,
            },

          },
          y: {
            title: {
              display: true,
              text: 'V/Vsat',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            border: {
              display: true,
              color: '#000000',
              width: NUMBERS_CONSTANTS.three
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 35
              },
              stepSize: 0.1
            },
            min: yMin,
            max: yMax,
            grid: {
              display: false,
            },
          },
        },
        elements: {
          point: {
            radius: 8,
            hoverRadius: 9,
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
    //For Pressure Liquid volume chart
    const pressureVolumeChart = new Chart(ctxPO2, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: '',
            data: objNewArray,
            borderColor: '#000000ff',
            backgroundColor: '#c55a11',
            showLine: false,
            fill: false,
            borderWidth: NUMBERS_CONSTANTS.three,
            pointStyle: 'circle',
            pointRadius: NUMBERS_CONSTANTS.eleven,
          },
        ],
      },
      options: {
        plugins: {
          title: {
            display: true,
            text: '',
            font: {
              size: 40,
              weight: 'bold'
            },
            color: '#000000'
          },
          legend: {
            display: false
          },
          datalabels: {
            display: false
          }
        },
        animation: false,
        scales: {
          x: {
            border: {
              display: true,
              color: '#000000',
              width: 3
            },
            title: {
              display: true,
              text: 'Pressure (psia)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            type: 'linear',
            position: 'bottom',
            ticks: {
              font: {
                weight: 'bold',
                size: 30
              },
              color: '#000000',
              stepSize: 2000,
              callback: (value) => value.toString()
            },
            min: 0,
            max: xMax,
            grid: {
              display: false,
            },

          },
          y: {
            title: {
              display: true,
              text: 'Liquid Vol (%)',
              font: {
                size: NUMBERS_CONSTANTS.thirtyFive,
                weight: 'bold'
              },
              color: '#000000'
            },
            border: {
              display: true,
              color: '#000000',
              width: 3
            },
            type: 'linear',
            beginAtZero: true,
            ticks: {
              color: '#000000',
              font: {
                weight: 'bold',
                size: 30
              },
              stepSize: 10
            },
            min: yMinLiquidVolume,
            max: yMaxLiquidVolume,
            grid: {
              display: false,
            },
          },
        }
      },
      plugins: [this.boxBorderPlugin, this.axisOriginTicksPlugin]
    });
    return [pressureV_VsatChart, pressureVolumeChart];
  }   

  GetAxisDefault(
    chart: Chart,
    fallbackXStep: number,
    fallbackYStep: number
  ): ChartAxisDefault {
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];

    return {
      xMin: Number(xScale.min),
      xMax: Number(xScale.max),
      xStep: this.GetTickStep(xScale.ticks, fallbackXStep),

      yMin: Number(yScale.min),
      yMax: Number(yScale.max),
      yStep: this.GetTickStep(yScale.ticks, fallbackYStep)
    };
  }

  GetTickStep(ticks: { value: number }[], fallbackStep: number): number {
    if (ticks.length >= NUMBERS_CONSTANTS.two) {
      return Number((ticks[NUMBERS_CONSTANTS.one].value - ticks[NUMBERS_CONSTANTS.zero].value).toFixed(NUMBERS_CONSTANTS.ten));
    }

    return fallbackStep;
  }

  BuildTicks(min: number, max: number, step: number): { value: number }[] {
    const ticks: { value: number }[] = [];

    if (step <= NUMBERS_CONSTANTS.zero) {
      ticks.push({ value: min });
      ticks.push({ value: max });
      return ticks;
    }

    const decimalPlaces = step.toString().includes('.')
      ? step.toString().split('.')[NUMBERS_CONSTANTS.one].length
      : NUMBERS_CONSTANTS.zero;

    for (let value = min; value <= max; value += step) {
      ticks.push({
        value: Number(value.toFixed(decimalPlaces))
      });
    }

    const lastTick = ticks[ticks.length - NUMBERS_CONSTANTS.one];

    if (lastTick && lastTick.value !== max) {
      ticks.push({
        value: Number(max.toFixed(decimalPlaces))
      });
    }

    return ticks;
  }

  ApplyAxis(
    chart: Chart,
    xMin: number,
    xMax: number,
    xStep: number,
    yMin: number,
    yMax: number,
    yStep: number
  ): void {
    const xScale = chart.options.scales?.['x'] as ScaleOptionsByType<'linear'>;
    const yScale = chart.options.scales?.['y'] as ScaleOptionsByType<'linear'>;

    if (xScale) {
      xScale.min = xMin;
      xScale.max = xMax;

      if (xScale.ticks) {
        xScale.ticks.autoSkip = false;
      }

      xScale.afterBuildTicks = (axis) => {
        axis.ticks = this.BuildTicks(xMin, xMax, xStep);
      };
    }

    if (yScale) {
      yScale.min = yMin;
      yScale.max = yMax;

      if (yScale.ticks) {
        yScale.ticks.autoSkip = false;
      }

      yScale.afterBuildTicks = (axis) => {
        axis.ticks = this.BuildTicks(yMin, yMax, yStep);
      };
    }
  }
 
}

